import { PlusIcon } from '@heroicons/react/24/outline';
import React from 'react';

import BookingRow from './BookingRow';
import { ColumnConfig, ColumnKey, ExtendedDatevBookingData } from './constants';
import HiddenColumnsToggle from './HiddenColumnsToggle';

interface BookingTableProps {
  data: ExtendedDatevBookingData[];
  columns: ColumnConfig[];
  visibleColumns: Set<ColumnKey>;
  isEditing: boolean;
  onRemove: (index: number) => void;
  onChangeCell: (
    index: number,
    field: ColumnKey,
    value: string | number | null
  ) => void;
  onAdd?: () => void;
  onToggleColumns: () => void;
}

const BookingTable: React.FC<BookingTableProps> = ({
  data,
  columns,
  visibleColumns,
  isEditing,
  onRemove,
  onChangeCell,
  onAdd,
  onToggleColumns,
}) => {
  const formatCurrency = (amount: number): string =>
    new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);

  return (
    <div className='bg-white rounded-lg border border-gray-200 overflow-visible'>
      <div className='p-3 border-b border-gray-200 flex justify-between items-center'>
        <span className='text-sm font-medium text-gray-700'>
          Booking Entries
        </span>
        <div className='flex items-center gap-2'>
          <HiddenColumnsToggle
            visibleColumns={visibleColumns}
            columnConfigs={columns}
            onToggle={onToggleColumns}
          />
        </div>
      </div>

      <div className='overflow-x-auto pb-2'>
        <table className='w-full min-w-max'>
          <thead className='bg-gray-100 border-b border-gray-200'>
            <tr>
              <th className='px-3 py-2 text-center text-xs font-semibold text-gray-700 border-r border-gray-200 whitespace-nowrap w-12'>
                #
              </th>
              {columns.map(
                col =>
                  visibleColumns.has(col.key) && (
                    <th
                      key={col.key}
                      className='px-3 py-2 text-left text-xs font-semibold text-gray-700 border-r border-gray-200 whitespace-nowrap'
                    >
                      {col.label}
                    </th>
                  )
              )}
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200'>
            {data.map((entry, index) => (
              <BookingRow
                key={index}
                entry={entry}
                index={index}
                isEditing={isEditing}
                columns={columns}
                visibleColumns={visibleColumns}
                onRemove={onRemove}
                onChangeCell={onChangeCell}
                formatCurrency={formatCurrency}
              />
            ))}
          </tbody>
        </table>

        {data.length === 0 && (
          <div className='p-8 text-center text-gray-500'>
            <p className='text-sm'>No booking entries</p>
          </div>
        )}
      </div>

      {isEditing && onAdd && (
        <div className='flex justify-end px-2 pt-2'>
          <button
            onClick={onAdd}
            className='text-sm text-blue-600 hover:text-blue-800 underline flex items-center gap-1'
          >
            <PlusIcon className='w-4 h-4' />
            Add entry
          </button>
        </div>
      )}
    </div>
  );
};

export default BookingTable;
